import routes from 'js/core_pre_webpack/routes';
import { keptAlivePost } from 'js/utils/kept_alive_post';
import { HIT_READY_EVENT } from 'js/utils/hitReady';
import { observeTrackingDelivery } from 'js/utils/observeTrackingDelivery';

type Category = 'content' | 'profile' | 'acquisition';
type Context = {
  version: number;
  surface: 'every_academic' | 'incumbent';
  profile_id: string;
  render_id: string;
  path: string;
  host: string;
};
const SELECTORS: Record<Category, string> = {
  content:
    '[data-profile-measurement-action="content"], .js-profile-measurement .js-work-strip-work-link, .js-profile-measurement [data-click-track^="profile-work-strip-download"]',
  profile:
    '[data-profile-measurement-action="profile"], .js-profile-measurement .js-profile-measurement-related-author a, .js-profile-measurement [data-click-track="profile-work-strip-authors"]',
  acquisition:
    '[data-profile-measurement-action="acquisition"], [data-click-track^="profile-signup-"], .js-profile-follow-button, .js-profile-related-author-follow',
};
const CATEGORIES = Object.keys(SELECTORS) as Category[];
let dispose: (() => void) | undefined;

function available(element: Element): boolean {
  if (element.closest('[hidden], [inert], [disabled], [aria-disabled="true"]'))
    return false;
  for (let node: Element | null = element; node; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }
  return true;
}

export function setUpProfileMeasurement(): void {
  if (dispose || typeof document === 'undefined') return;
  const serialized =
    document.querySelector<HTMLScriptElement>(
      'script[data-profile-measurement]',
    )?.textContent ??
    document.querySelector<HTMLMetaElement>('meta[name="profile-measurement"]')
      ?.content;
  if (!serialized) return;
  let context: Context;
  try {
    context = JSON.parse(serialized) as Context;
    if (!context || typeof context !== 'object') return;
  } catch {
    return;
  }
  if (
    context.version !== 1 ||
    !['every_academic', 'incumbent'].includes(context.surface) ||
    !context.profile_id ||
    !context.render_id ||
    context.path !== location.pathname ||
    context.host !== location.hostname
  )
    return;

  const categories = (): Category[] =>
    CATEGORIES.filter((category) =>
      Array.from(document.querySelectorAll(SELECTORS[category])).some(
        available,
      ),
    );
  let sequence = 0;
  let viewSent = false;
  const reportedCategories = new Set<Category>();
  const queue: {
    event: string;
    id: string;
    category: Category | null;
    at: number;
    available: Category[];
  }[] = [];

  const flush = (allowMissing = false): void => {
    const hitId = window.Aedu?.hit_data?.id;
    if (!hitId && !allowMissing) return;
    while (queue.length) {
      const item = queue.shift();
      if (!item) continue;
      void observeTrackingDelivery('profile_measurement', () =>
        keptAlivePost(
          routes.api_v0_arbitrary_event_index_path({ subdomain_param: 'api' }),
          {
            event_type: 'ProfileMeasurement',
            hit_id: hitId ?? null,
            data: {
              event_name: item.event,
              event_id: item.id,
              context,
              category: item.category,
              available_categories: item.available,
              client_occurred_at: item.at,
              hit_missing: !hitId,
            },
          },
        ),
      );
    }
  };
  const enqueue = (event: string, category: Category | null): void => {
    if (
      context.path !== location.pathname ||
      context.host !== location.hostname
    )
      return;
    const current = categories();
    if (
      event === 'availability' &&
      current.every((name) => reportedCategories.has(name))
    )
      return;
    current.forEach((name) => reportedCategories.add(name));
    if (reportedCategories.size === CATEGORIES.length) {
      observer.disconnect();
      clearTimeout(pendingAvailability);
      pendingAvailability = undefined;
    }
    if (queue.length >= 100) flush(true);
    queue.push({
      event,
      category,
      id: `${context.render_id}:${sequence++}`,
      at: Date.now(),
      available: current,
    });
    flush();
  };
  const onReady = (): void => {
    if (
      context.path !== location.pathname ||
      context.host !== location.hostname
    ) {
      stopProfileMeasurement();
      return;
    }
    if (!viewSent) {
      viewSent = true;
      enqueue('view', null);
    }
    flush();
  };
  const onClick = (event: Event): void => {
    const element = event.target instanceof Element ? event.target : null;
    if (!element) return;
    const category = CATEGORIES.find((name) => {
      const target = element.closest(SELECTORS[name]);
      return target && available(target);
    });
    if (category) enqueue('click', category);
  };
  const onPageHide = (): void => flush(true);
  let pendingAvailability: ReturnType<typeof setTimeout> | undefined;
  const observer = new MutationObserver(() => {
    if (pendingAvailability) return;
    pendingAvailability = setTimeout(() => {
      pendingAvailability = undefined;
      enqueue('availability', null);
    }, 0);
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden', 'disabled', 'aria-disabled'],
  });
  document.addEventListener('click', onClick, true);
  window.addEventListener(HIT_READY_EVENT, onReady);
  window.addEventListener('pagehide', onPageHide);
  dispose = () => {
    observer.disconnect();
    clearTimeout(pendingAvailability);
    document.removeEventListener('click', onClick, true);
    window.removeEventListener(HIT_READY_EVENT, onReady);
    window.removeEventListener('pagehide', onPageHide);
  };
  onReady();
}

export function stopProfileMeasurement(): void {
  dispose?.();
  dispose = undefined;
}
